import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useToast from 'hooks/useToast'
import { postDeal } from 'submission/lib/deal-api'
import {
  initialEquityFormData,
  type EquityFormData,
} from 'submission/lib/equity-form-defaults'
import { mapEquityFormToPayload } from 'submission/lib/equity-form-mapper'
import { validateEquityForm } from 'submission/lib/equity-form-validators'
import OverviewSection from './sections/OverviewSection'
import VehicleSection from './sections/VehicleSection'
import TermsSection from './sections/TermsSection'
import TimelineSection from './sections/TimelineSection'
import BasePdfsSection from './sections/BasePdfsSection'
import ReferralSection from './sections/ReferralSection'
import FundsSection from './sections/FundsSection'
import VisibilitySection from './sections/VisibilitySection'
import AccessorySection from './sections/AccessorySection'
import NftSection from './sections/NftSection'

const EquitySubmissionForm: React.FC = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [form, setForm] = useState<EquityFormData>(initialEquityFormData)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async () => {
    const v = validateEquityForm(form)
    setErrors(v)
    if (Object.keys(v).length > 0) {
      showToast({
        type: 'error',
        message: 'Please fix the highlighted fields',
      })
      return
    }
    setSubmitting(true)
    try {
      const payload = mapEquityFormToPayload(form)
      const res = await postDeal(payload)
      const dealId = res?.data?.id ?? res?.id
      showToast({ type: 'success', message: 'Equity deal created' })
      if (dealId) navigate(`/deals/${dealId}`)
    } catch (e: any) {
      showToast({
        type: 'error',
        message: `Submit failed: ${e?.message ?? 'unknown'}`,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const hasErrors = Object.keys(errors).length > 0

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="equity-form">
      <h1 className="text-2xl font-semibold text-slate-900 mb-6">
        Create equity deal
      </h1>
      <div className="flex flex-col gap-6">
        <OverviewSection form={form} setForm={setForm} errors={errors} />
        <VehicleSection form={form} setForm={setForm} errors={errors} />
        <TermsSection form={form} setForm={setForm} errors={errors} />
        <TimelineSection form={form} setForm={setForm} errors={errors} />
        <BasePdfsSection form={form} setForm={setForm} errors={errors} />
        <ReferralSection form={form} setForm={setForm} errors={errors} />
        <FundsSection form={form} setForm={setForm} errors={errors} />
        <VisibilitySection form={form} setForm={setForm} errors={errors} />
        <AccessorySection form={form} setForm={setForm} />
        <NftSection form={form} setForm={setForm} errors={errors} />

        {hasErrors && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
            <strong>Please fix:</strong>
            <ul className="list-disc ml-5 mt-1">
              {Object.entries(errors).map(([k, msg]) => (
                <li key={k}>
                  {k}: {msg}
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          type="button"
          className="self-end px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
          disabled={submitting}
          onClick={onSubmit}
        >
          {submitting ? 'Submitting…' : 'Submit deal'}
        </button>
      </div>
    </div>
  )
}

export default EquitySubmissionForm
